import {Spot} from "../types";
import {buildSpotRuleGraph, SpotRuleGraph} from "../../pages/areaDetails/customFlow/spotRuleGraph";

export type CycleDetectionResult = {
    hasCycle: boolean;
    cycles: string[][]; // Each cycle is an array of node IDs forming the loop
};

/**
 * Detect cycles in the spot rule graph using DFS with coloring.
 * Returns all distinct cycles found.
 */
export const detectCycles = (spots: Spot[]): CycleDetectionResult => {
    const graph = buildSpotRuleGraph(spots);
    return detectCyclesInGraph(graph);
};

export const detectCyclesInGraph = (graph: SpotRuleGraph): CycleDetectionResult => {
    const WHITE = 0; // unvisited
    const GRAY = 1;  // in current DFS path
    const BLACK = 2; // fully processed

    const color = new Map<string, number>();
    const parent = new Map<string, string | null>();
    const cycles: string[][] = [];

    graph.spotByNodeId.forEach((_, nodeId) => {
        color.set(nodeId, WHITE);
    });

    const dfs = (nodeId: string, path: string[]) => {
        color.set(nodeId, GRAY);
        path.push(nodeId);

        const outgoing = graph.outgoingByNodeId.get(nodeId) || [];
        for (const edge of outgoing) {
            const neighbor = edge.toId;
            const neighborColor = color.get(neighbor);

            if (neighborColor === GRAY) {
                // Back edge — extract the cycle
                const cycleStart = path.indexOf(neighbor);
                if (cycleStart !== -1) {
                    cycles.push(path.slice(cycleStart));
                }
            } else if (neighborColor === WHITE) {
                dfs(neighbor, path);
            }
        }

        path.pop();
        color.set(nodeId, BLACK);
    };

    graph.spotByNodeId.forEach((_, nodeId) => {
        if (color.get(nodeId) === WHITE) {
            dfs(nodeId, []);
        }
    });

    return {
        hasCycle: cycles.length > 0,
        cycles,
    };
};

/**
 * Get human-readable cycle descriptions using spot names.
 */
export const describeCycles = (spots: Spot[], result: CycleDetectionResult): string[] => {
    if (!result.hasCycle) return [];

    const graph = buildSpotRuleGraph(spots);
    return result.cycles.map((cycle) => {
        const names = cycle.map((nodeId) => {
            const spot = graph.spotByNodeId.get(nodeId);
            return spot?.name || nodeId;
        });
        return names.join(" -> ") + " -> " + names[0];
    });
};
